import './UpgradeModal.scss'
import { Modal } from '../../../components/Modal/Modal'
import React, { useEffect, useState } from 'react'
import { BillingCustomer, Plan, PricePeriod } from '../../../models/Plan'
import { Col, Container, Modal as BootstrapModal, Row, Spinner } from 'react-bootstrap'
import { SubscriptionChangeStatus, useChangePlanMutation, useCreateInvoicePreviewMutation } from '../../../store/plan/plan'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { SeatUpgradeBody } from './SeatUpgradeBody'
import { PlanUpgradeBody } from './PlanUpgradeBody'
import { PeriodUpgradeBody } from './PeriodUpgradeBody'
import classNames from 'classnames'
import { CurrencyDiv } from '../CurrencyDiv'
import { formatDateDDMMYYYY } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { useUsedSeats } from '../../../common/useUsedSeats'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'

export type UpgradeModalType = 'period' | 'seat' | 'assistant' | 'plan'

export interface UpgradeModalProps {
  initialType: UpgradeModalType
  show: boolean
  onHide: () => void
  onSave: () => void
  period: PricePeriod
  plan: Plan
  freeTrial: boolean
  plans: Plan[]
  customer: BillingCustomer
}

export const UpgradeModal: React.FC<UpgradeModalProps> = ({ initialType, show, onHide, onSave, period, plan, freeTrial, plans, customer }) => {
  const { tracked } = useTracking()
  const accountId = useAppSelector(selectAccountId)
  const [changePlan, { isLoading: isUpdating }] = useChangePlanMutation()
  const [createInvoicePreview, { isLoading: previewLoading, data: createInvoicePreviewResponse }] = useCreateInvoicePreviewMutation()
  const [type] = useState<UpgradeModalType>(initialType)
  const usedSeats = useUsedSeats()
  const [selectedNumberOfSeats, setSelectedNumberOfSeats] = useState<number | null>(customer.subscription?.numberOfSeats ?? usedSeats)
  const { getQuotaValue } = useZocksQuota()
  const freeAssistantSeatMultiplier = Number(getQuotaValue('identity.sidecarSeats.value'))
  const freeAssistantSeats = (selectedNumberOfSeats ?? 0) * freeAssistantSeatMultiplier
  const [selectedNumberOfAssistants, setSelectedNumberOfAssistants] = useState<number | null>(
    customer.subscription?.additionalProducts['assistant']?.quantity ?? 0,
  )
  const totalAssistantSeats = (selectedNumberOfAssistants ?? 0) + freeAssistantSeats
  const [selectedPlan, setSelectedPlan] = useState<Plan>(plan)
  const [selectedPeriod, setSelectedPeriod] = useState<PricePeriod>(period)
  const customerPlan = plans.find((plan) => plan.id === customer.subscription?.planId)
  const [selectedPaymentType, setSelectedPaymentType] = useState<string>(customer.subscription?.paymentType ?? 'autoCharge')
  const isInFreeTrial = (plan: Plan) => {
    if (customer) {
      return plan.id === customerPlan?.id && customer.freeTrial && customer.freeTrial.startedAt && !customer.freeTrial.endedAt
    }
    return false
  }

  const autoChargeDisabled = selectedPaymentType === 'autoCharge' && customer.subscription?.paymentType === 'emailInvoice' && !customer.hasPaymentMethod

  const hideBillingInfo = !!customer.enterpriseSubscription?.enterpriseBillingConfig?.package.hideBillingInfo

  const isUpgrade =
    !customerPlan ||
    !customer.subscription ||
    isInFreeTrial(selectedPlan) ||
    (selectedNumberOfSeats ?? 0) > customer.subscription.numberOfSeats ||
    (selectedNumberOfAssistants ?? 0) > (customer.subscription.additionalProducts['assistant']?.quantity ?? 0) ||
    selectedPlan.tier > customerPlan.tier

  const handleNumberOfSeatsChange = tracked(
    (newValue: number) => {
      if (newValue > 0) {
        setSelectedNumberOfSeats(newValue)
      } else {
        setSelectedNumberOfSeats(null)
      }
    },
    { eventName: TrackEvent.PLAN_UPGRADE_SEAT_CHANGED },
    {
      eventDetailsExtractor: (newValue: number) => {
        return { seat: newValue }
      },
    },
  )

  const handleNumberOfAssistantsChange = tracked(
    (newValue: number) => {
      if (newValue > freeAssistantSeats) {
        setSelectedNumberOfAssistants(newValue - freeAssistantSeats)
      } else {
        setSelectedNumberOfAssistants(0)
      }
    },
    { eventName: TrackEvent.PLAN_UPGRADE_ASSISTANT_SEAT_CHANGED },
    {
      eventDetailsExtractor: (newValue: number) => {
        return { seat: newValue - freeAssistantSeats }
      },
    },
  )

  const handlePlanChange = tracked(
    (newPlan: Plan) => {
      setSelectedPlan(newPlan)
    },
    { eventName: TrackEvent.PLAN_UPGRADE_PLAN_CHANGED },
    {
      eventDetailsExtractor: (newPlan: Plan) => {
        return { plan: newPlan.id }
      },
    },
  )

  useEffect(() => {
    if (isUpgrade && !isInFreeTrial(selectedPlan) && !hideBillingInfo) {
      if (selectedNumberOfSeats && customer.subscription && customerPlan) {
        createInvoicePreview({
          accountId,
          payload: {
            ...((selectedPlan.id !== customerPlan.id || (customer.upcomingSubscription && selectedPlan.id !== customer.upcomingSubscription.planId)) && {
              planId: selectedPlan.id,
            }),
            ...((selectedPeriod !== customer.subscription.period ||
              (customer.upcomingSubscription && selectedPeriod !== customer.upcomingSubscription.period)) && { period: selectedPeriod }),
            ...((customer.upcomingSubscription
              ? selectedNumberOfSeats !== customer.upcomingSubscription.numberOfSeats
              : selectedNumberOfSeats !== customer.subscription.numberOfSeats) && { numberOfSeats: selectedNumberOfSeats }),
            ...((customer.upcomingSubscription
              ? selectedNumberOfAssistants !== customer.upcomingSubscription.additionalProducts['assistant']?.quantity
              : selectedNumberOfAssistants !== customer.subscription.additionalProducts['assistant']?.quantity) && {
              additionalProductChanges: { assistant: { quantity: selectedNumberOfAssistants ?? 0 } },
            }),
          },
        })
      }
    }
  }, [isUpgrade, hideBillingInfo, selectedPlan, selectedPeriod, selectedNumberOfSeats, selectedNumberOfAssistants])

  const onUpgradePlan = tracked(
    async () => {
      if (selectedNumberOfSeats) {
        try {
          const p = {
            accountId,
            payload: {
              ...((!customerPlan ||
                selectedPlan.id !== customerPlan.id ||
                (customer.upcomingSubscription && selectedPlan.id !== customer.upcomingSubscription.planId)) && {
                planId: selectedPlan.id,
              }),
              ...((!customer.subscription ||
                selectedPeriod !== customer.subscription.period ||
                (customer.upcomingSubscription && selectedPeriod !== customer.upcomingSubscription.period)) && { period: selectedPeriod }),
              ...((customer.upcomingSubscription
                ? selectedNumberOfSeats !== customer.upcomingSubscription.numberOfSeats
                : !customer.subscription || selectedNumberOfSeats !== customer.subscription.numberOfSeats) && { numberOfSeats: selectedNumberOfSeats }),
              ...((customer.upcomingSubscription
                ? selectedNumberOfAssistants !== customer.upcomingSubscription.additionalProducts['assistant']?.quantity
                : !customer.subscription || selectedNumberOfAssistants !== customer.subscription.additionalProducts['assistant']?.quantity) && {
                additionalProductChanges: { assistant: { quantity: selectedNumberOfAssistants ?? 0 } },
              }),
              ...(!isEnterpriseAggregate && { paymentType: selectedPaymentType }),
              freeTrial:
                freeTrial ||
                (customerPlan &&
                  customer.subscription &&
                  (selectedPlan.id !== customerPlan.id ||
                    (customerPlan.type !== 'free' &&
                      (selectedNumberOfSeats !== customer.subscription.numberOfSeats ||
                        selectedNumberOfAssistants !== customer.subscription.additionalProducts['assistant']?.quantity))) &&
                  customer.freeTrial &&
                  !customer.freeTrial.endedAt),
            },
          }

          const response = await changePlan(p).unwrap()
          if (response.data.result === SubscriptionChangeStatus.CHECKOUT_REQUIRED.valueOf()) {
            window.location.href = response.data.checkoutSessionUrl
          } else {
            onSave()
          }
        } catch (e) {}
      }
    },
    {
      eventName: TrackEvent.PLAN_UPGRADE_CLICKED,
      eventDetails: { plan: selectedPlan.id, period: selectedPeriod, seat: selectedNumberOfSeats, assistant: selectedNumberOfAssistants },
    },
  )
  const typeMap = {
    period: <PeriodUpgradeBody period={selectedPeriod} onPeriodChange={setSelectedPeriod} />,
    seat: <SeatUpgradeBody numberOfSeats={selectedNumberOfSeats} onNumberOfSeatsChange={handleNumberOfSeatsChange} customerPlan={customerPlan || null} />,
    assistant: (
      <SeatUpgradeBody numberOfSeats={totalAssistantSeats} onNumberOfSeatsChange={handleNumberOfAssistantsChange} customerPlan={customerPlan || null} />
    ),
    plan: (
      <PlanUpgradeBody
        customer={customer}
        plan={selectedPlan}
        plans={plans}
        period={selectedPeriod}
        paymentType={selectedPaymentType}
        autoChargeDisabled={autoChargeDisabled}
        onPlanChange={handlePlanChange}
        onPaymentTypeChange={setSelectedPaymentType}
      />
    ),
  }

  const typeTitleMap = {
    period: 'Manage your plan period',
    seat: 'Manage your seats',
    assistant: 'Manage your assistant seats',
    plan: freeTrial ? 'Start free trial' : 'Manage your plan',
  }

  const header = <BootstrapModal.Title>{typeTitleMap[type]}</BootstrapModal.Title>

  const saveDisabled =
    (type === 'seat' && (selectedPlan.type === 'free' || !selectedNumberOfSeats || selectedNumberOfSeats === customer.subscription?.numberOfSeats)) ||
    (type === 'assistant' &&
      (selectedPlan.type === 'free' || selectedNumberOfAssistants === customer.subscription?.additionalProducts['assistant']?.quantity)) ||
    autoChargeDisabled

  const selectedPlansPrice = selectedPlan.prices.find((price) => price.period === selectedPeriod)!
  const selectedPlanPriceAmount = selectedPlansPrice.billingScheme === 'perUnit' ? selectedPlansPrice.amount : 0
  const selectedAssistantProduct = selectedPlan.additionalProducts.find((additionalProduct) => additionalProduct.type === 'assistant')
  const selectedPlanAssistantPrice = selectedAssistantProduct ? selectedAssistantProduct.prices.find((price) => price.period === selectedPeriod) : null

  const isEnterpriseAggregate = customer.enterpriseSubscription?.enterpriseBillingConfig?.package.type === 'aggregate'

  const body = (
    <Container>
      <Row className='zocks-mb-1.5'>
        <Col>
          <div className={classNames('upgrade-info-container', { 'upgrade-info-container--active': type === 'period' })}>
            <div className='lead-extra-small'>Period</div>
            <h6>{selectedPeriod === 'month' ? 'Monthly' : 'Annual'}</h6>
          </div>
        </Col>
        <Col>
          <div className={classNames('upgrade-info-container', { 'upgrade-info-container--active': type === 'seat' })}>
            <div className='text-nowrap lead-extra-small'>Number of seats</div>
            <h6>{selectedNumberOfSeats ? selectedNumberOfSeats : '0'}</h6>
          </div>
        </Col>
        {selectedAssistantProduct && (
          <Col>
            <div className={classNames('upgrade-info-container', { 'upgrade-info-container--active': type === 'assistant' })}>
              <div className='text-nowrap lead-extra-small'>Number of assistants</div>
              <h6>{totalAssistantSeats}</h6>
            </div>
          </Col>
        )}
        <Col>
          <div className={classNames('upgrade-info-container', { 'upgrade-info-container--active': type === 'plan' })}>
            <div className='lead-extra-small'>Plan</div>
            <h6>{selectedPlan.name}</h6>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>{typeMap[type]}</Col>
      </Row>
      <Row>
        <Col>
          {type !== 'seat' && type !== 'assistant' && customerPlan?.type === 'free' && (
            <div className='create-invoice-preview-container-next-billing'>
              {customer.subscription ? (
                <div className='lead-extra-small'>New plan starting on {formatDateDDMMYYYY(customer.subscription.currentPeriodEnd)}</div>
              ) : (
                <div className='lead-extra-small'>New plan starting now</div>
              )}
              <div className='d-flex justify-content-between'>
                <div>
                  {selectedPlan.name} * {selectedNumberOfSeats} seat(s) * ${selectedPlanPriceAmount / 100}{' '}
                  {selectedPlanAssistantPrice ? '+ assistant seats * ' + selectedNumberOfAssistants + ' * $' + selectedPlanPriceAmount / 100 + ' = ' : ''}
                </div>
                <div>
                  ${(selectedNumberOfSeats || 0) * (selectedPlanPriceAmount / 100) + ((selectedNumberOfAssistants ?? 0) * (selectedPlanPriceAmount || 0)) / 100}
                </div>
              </div>
            </div>
          )}
          {((customer && customer.freeTrial && customer.freeTrial.endedAt) ||
            (customer.subscription?.planId === selectedPlan.id && type !== 'seat' && type !== 'assistant')) && (
            <div className='create-invoice-preview-container mt-2'>
              <>
                {!isEnterpriseAggregate && !hideBillingInfo ? (
                  <div>
                    <div className='lead-extra-small'>Due today</div>
                    {previewLoading && customer.subscription && !createInvoicePreviewResponse ? (
                      <div className='d-flex align-items-center justify-content-center'>
                        <Spinner />
                      </div>
                    ) : (
                      <>
                        <h1>
                          <CurrencyDiv>$</CurrencyDiv>
                          {!customer.subscription
                            ? (selectedNumberOfSeats || 0) * (selectedPlanPriceAmount / 100) +
                              ((selectedNumberOfAssistants ?? 0) * (selectedPlanPriceAmount || 0)) / 100
                            : isUpgrade && !isInFreeTrial(selectedPlan) && createInvoicePreviewResponse
                              ? createInvoicePreviewResponse.data.amountDue / 100
                              : 0}
                        </h1>
                        {createInvoicePreviewResponse && createInvoicePreviewResponse.data.startingBalance ? (
                          <div className='lead-extra-small'>
                            (Used credits: ${((createInvoicePreviewResponse.data.startingBalance - createInvoicePreviewResponse.data.endingBalance) / 100) * -1}
                            )
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>
                ) : (
                  'Billing goes through your enterprise account.'
                )}
              </>
            </div>
          )}
          {customer.subscription &&
            !(type === 'seat' && customer.subscription.numberOfSeats === selectedNumberOfSeats) &&
            !(type === 'assistant' && customer.subscription.additionalProducts['assistant'] === selectedNumberOfAssistants) &&
            !isUpgrade &&
            !isEnterpriseAggregate && (
              <div className='downgrade-container lead-extra-small'>
                <i className='bi bi-exclamation-circle-fill' /> This change will take effect at the end of your period:{' '}
                {formatDateDDMMYYYY(customer.subscription.currentPeriodEnd)}
              </div>
            )}
        </Col>
      </Row>
    </Container>
  )
  return (
    <Modal
      className='upgrade-modal'
      show={show}
      onHide={onHide}
      header={header}
      body={body}
      showFooterActions={true}
      centered={true}
      onFooterPrimaryAction={onUpgradePlan}
      footerPrimaryActionLoading={isUpdating}
      footerPrimaryActionDisabled={saveDisabled}
    />
  )
}
